// A team's colors for its manager's pages. The header and footer tab bar
// take the team's primary color (color1); text on it uses the team's
// second color when it reads clearly, else white or near-black.
// College (light page): buttons, table heads, links and borders use color1
// too, darkened when it's too light to read on white.
// Pro (dark page): buttons, links and accents use color1 when it shows up
// on the dark background (else color2, unless that's plain white or
// black), lightened until it reads.
import { normalizeColor, textColorOn } from '@shared/teamList.js';

// The page surfaces (themes.scss): varsity (college) and arena (pro).
const SURFACE = '#ffffff';
const DARK_SURFACE = '#131d30';

function rgb(hex) {
    return [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));
}

function hexOf([r, g, b]) {
    return `#${[r, g, b].map(v => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('')}`;
}

// WCAG relative luminance and contrast ratio.
function luminance(hex) {
    const [r, g, b] = rgb(hex).map(v => {
        const c = v / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrast(a, b) {
    const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
    return (hi + 0.05) / (lo + 0.05);
}

// `hex` mixed toward `to` by share (0-1).
function mix(hex, to, share) {
    const a = rgb(hex);
    const b = rgb(to);
    return hexOf(a.map((v, i) => v + (b[i] - v) * share));
}

// Darkens (or, on a dark background, lightens) a color in steps until it
// reads on `against` at `ratio`.
function readableOn(hex, against, ratio) {
    const toward = luminance(against) > 0.4 ? '#000000' : '#ffffff';
    let c = hex;
    for (let i = 0; i < 12 && contrast(c, against) < ratio; i++) c = mix(c, toward, 0.15);
    return c;
}

// CSS variables for an element's style attribute, or null without colors.
// dark: the pro theme (dark page).
export function teamThemeVars(team, { dark = false } = {}) {
    const c1 = normalizeColor(team?.color1);
    if (!c1) return null;
    const c2 = normalizeColor(team?.color2);
    const onC1 = c2 && contrast(c2, c1) >= 3 ? c2 : textColorOn(c1);
    if (dark) {
        // The team's main color when it shows up on the dark page; else
        // its second color, unless that's plain white or black; lifted
        // until text in it reads there.
        const plain = (c) => luminance(c) > 0.85 || luminance(c) < 0.01;
        const base = contrast(c1, DARK_SURFACE) >= 3 || !c2 || plain(c2) ||
            contrast(c2, DARK_SURFACE) < 3 ? c1 : c2;
        const accent = readableOn(base, DARK_SURFACE, 4.5);
        const [r, g, b] = rgb(accent);
        return {
            '--header-bg': c1,
            '--header-text': onC1,
            '--logo-accent': onC1,
            '--primary': accent,
            '--primary-light': accent,
            '--link': accent,
            '--table-head-text': accent,
            '--btn-bg': accent,
            '--btn-bg-2': mix(accent, '#ffffff', 0.2),
            '--btn-text': textColorOn(accent),
            '--bg-image': `radial-gradient(ellipse at 50% -10%, rgba(${r}, ${g}, ${b}, .08), transparent 60%)`,
        };
    }
    // Normal-size text on the page needs 4.5:1.
    const ink = readableOn(c1, SURFACE, 4.5);
    const [r, g, b] = rgb(ink);
    return {
        '--header-bg': c1,
        '--header-text': onC1,
        '--table-head-bg': c1,
        '--table-head-text': onC1,
        '--btn-bg': c1,
        '--btn-bg-2': mix(c1, contrast(c1, '#000000') > contrast(c1, '#ffffff') ? '#000000' : '#ffffff', 0.18),
        '--btn-text': onC1,
        '--primary': ink,
        '--primary-light': ink,
        '--link': ink,
        '--card-border': `2px solid ${ink}`,
        '--card-shadow': `0 3px 0 rgba(${r}, ${g}, ${b}, .25)`,
    };
}
